import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography, IconButton, TableContainer, Paper
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

export function TestDetailModal({ open, onClose, test, skis }) {
  const [skiDetail, setSkiDetail] = useState(null);
  
  if (!test) return null;
  const skiMap = Object.fromEntries((skis || []).map(ski => [ski.id, ski]));
  const rankedSkis = (test.ski_ids_ranked || []).map((id, idx) => ({ rank: idx + 1, ...skiMap[id] }));
  
  // Determine column headers based on brands in the test
  const brandsInTest = [...new Set(rankedSkis.map(ski => ski.brand))];
  const hasFischer = brandsInTest.includes('Fischer');
  const hasSalomonAtomic = brandsInTest.some(brand => ['Salomon', 'Atomic'].includes(brand));
  
  let flexHeader, camberHeader;
  if (hasFischer && !hasSalomonAtomic) {
    // Only Fischer skis
    flexHeader = 'FA';
    camberHeader = 'HR';
  } else if (hasSalomonAtomic && !hasFischer) {
    // Only Salomon/Atomic skis
    flexHeader = 'MF';
    camberHeader = 'H4';
  } else {
    // Mixed brands or other brands
    flexHeader = 'Flex';
    camberHeader = 'Camber';
  }
  
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth>
        <DialogTitle>Test Details</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Typography variant="subtitle1"><b>Name:</b> {test.name}</Typography>
          <Typography variant="subtitle1"><b>Location:</b> {test.location}</Typography>
          <Typography variant="subtitle1"><b>Date:</b> {test.date ? test.date.split('T')[0] : ''}</Typography>
          <Typography variant="subtitle1"><b>Technique:</b> {test.technique}</Typography>
          <Typography variant="subtitle1"><b>Notes:</b> {test.notes}</Typography>
          <Typography variant="subtitle1" sx={{ mt: 2 }}><b>Ranked Skis:</b></Typography>
          <TableContainer component={Paper} sx={{ mt: 1, boxShadow: 1, width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 650, width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Ski #</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>{flexHeader}</TableCell>
                  <TableCell>{camberHeader}</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center" sx={{ pr: 0 }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rankedSkis.map((ski, idx) => {
                  const isFischer = ski.brand === 'Fischer';
                  const isSalomonAtomic = ['Salomon', 'Atomic'].includes(ski.brand);
                  const flexValue = isFischer ? ski.fa : (isSalomonAtomic ? ski.mf : '');
                  const camberValue = isFischer ? ski.hr : (isSalomonAtomic ? ski.h4 : '');
                  
                  return (
                    <TableRow key={ski.id || idx}>
                      <TableCell>{ski.rank}</TableCell>
                      <TableCell>{ski.ski_number}</TableCell>
                      <TableCell>{ski.brand}</TableCell>
                      <TableCell>{flexValue}</TableCell>
                      <TableCell>{camberValue}</TableCell>
                      <TableCell>{ski.type}</TableCell>
                      <TableCell align="center" sx={{ pr: 0 }}>
                        <IconButton size="small" onClick={() => setSkiDetail(ski)}>
                          <InfoIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Ski Detail Modal */}
      <Dialog open={!!skiDetail} onClose={() => setSkiDetail(null)} maxWidth={false} fullWidth PaperProps={{ sx: { maxWidth: 800 } }}>
        <DialogTitle>Ski #{skiDetail?.ski_number} Details</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ my: 2, borderRadius: 2, boxShadow: 1 }}>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Brand</TableCell><TableCell>{skiDetail?.brand}</TableCell></TableRow>
                {skiDetail?.brand === 'Fischer' && (
                  <>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>FA (Flex)</TableCell><TableCell>{skiDetail?.fa}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>HR (Camber Height)</TableCell><TableCell>{skiDetail?.hr}</TableCell></TableRow>
                  </>
                )}
                {['Salomon', 'Atomic'].includes(skiDetail?.brand) && (
                  <>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>MF (Flex)</TableCell><TableCell>{skiDetail?.mf}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>H4 (Camber Height)</TableCell><TableCell>{skiDetail?.h4}</TableCell></TableRow>
                  </>
                )}
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Grind</TableCell><TableCell>{skiDetail?.grind}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Mold</TableCell><TableCell>{skiDetail?.mold}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Year Made</TableCell><TableCell>{skiDetail?.year_made}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Type</TableCell><TableCell>{skiDetail?.type}</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkiDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
} 